import { useEffect, useId, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import ChevronDownIcon from 'app/assets/chevron-down-icon.svg';
import ChevronUpIcon from 'app/assets/chevron-up-icon.svg';
import { LeadStatusPill } from 'app/features/leads/components/lead-status-pill';
import { leadDetailQueryOptions } from 'app/features/leads/queries/lead-detail-query-options';
import { updateLeadStatusMutationOptions } from 'app/features/leads/queries/update-lead-status-mutation-options';
import { LeadsQueryProvider } from 'app/features/leads/providers/leads-query-provider';
import type { LeadStatus, LeadSummary } from 'app/features/leads/services/gym-finder/types';
import { formatLeadInterestText } from 'app/features/leads/utils/format-lead-interest';
import { routes } from 'app/routes/constants';
import Button from 'app/ui/components/atoms/button';
import Typography from 'app/ui/components/atoms/typography';
import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import useServices from 'app/services';
import { trackEvent } from 'app/utils/analytics/tracker';

const STATUS_OPTIONS: LeadStatus[] = ['new', 'contacted', 'attended'];

const PageContent = styled.div`
  padding: ${({ theme: { spacing } }) => `${spacing(4)} 0 ${spacing(12)}`};
`;

const Breadcrumb = styled.nav`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(1)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(8)};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.xsmall};
`;

const BreadcrumbLink = styled(Link)`
  color: ${({ theme: { colors } }) => colors.knop};
  text-decoration: none;

  &:hover,
  &:focus {
    color: ${({ theme: { colors } }) => colors.focoltone};
  }
`;

export const LeadBreadcrumbCurrent = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme: { colors } }) => colors.focoltone};
`;

const ProfileHeader = styled.header`
  display: flex;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(10)};
`;

export const LeadInitialsAvatar = styled.div`
  width: ${({ theme: { spacing } }) => spacing(25)};
  height: ${({ theme: { spacing } }) => spacing(25)};
  flex-shrink: 0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme: { colors } }) => colors.charcoal};
  color: ${({ theme: { colors } }) => colors.focoltone};
  font-family: ${({ theme: { typography } }) => typography.poppins};
  font-size: ${({ theme: { typography } }) => typography.jumbo};
  font-weight: ${({ theme: { typography } }) => typography.bold};
`;

export const LeadHeaderText = styled.div`
  flex: 1 1 auto;
  min-width: 0;
`;

export const LeadTitle = styled(Typography)`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const ContactText = styled(Typography)`
  color: ${({ theme: { colors } }) => colors.lightGray};
`;

const ContactDivider = styled.span`
  width: 0.0625rem;
  height: ${({ theme: { spacing } }) => spacing(5)};
  background: ${({ theme: { colors } }) => colors.blackSwan};
`;

const DetailGrid = styled.div`
  max-width: ${({ theme: { spacing } }) => spacing(161)};
`;

const SectionTitle = styled(Typography)`
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const DetailRows = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const DetailRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme: { spacing } }) => spacing(3)};
  border-bottom: 0.0625rem solid ${({ theme: { colors } }) => colors.veryGray};
  padding: ${({ theme: { spacing } }) => `0 ${spacing(1)} ${spacing(2.5)}`};
`;

const DetailValue = styled(Typography)`
  font-weight: ${({ theme: { typography } }) => typography.bold};
  text-align: right;
`;

const NoteBlock = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(5)};
`;

const NoteLabel = styled(Typography)`
  display: block;
  margin-bottom: ${({ theme: { spacing } }) => spacing(2)};
`;

const StatusField = styled.div`
  margin-bottom: ${({ theme: { spacing } }) => spacing(3.5)};
`;

const StatusLabel = styled(Typography)`
  display: block;
  margin-bottom: ${({ theme: { spacing } }) => spacing(3)};
`;

const StatusSelectContainer = styled.div`
  width: 100%;
`;

const StatusSelectButton = styled.button<{ disabled: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(4)}`};
  border: 0.0625rem solid ${({ theme: { colors } }) => colors.blackSwan};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  background: ${({ theme: { colors }, disabled }) => (disabled ? colors.veryGray : colors.black)};

  img {
    filter: invert(1);
  }
`;

const StatusOptions = styled.ul`
  margin: ${({ theme: { spacing } }) => `${spacing(2)} 0 0`};
  padding: 0;
  overflow: hidden;
  border: 0.0625rem solid ${({ theme: { colors } }) => colors.blackSwan};
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
`;

const StatusOption = styled.li`
  list-style: none;
  border-bottom: 0.0625rem solid ${({ theme: { colors } }) => colors.blackSwan};

  &:last-child {
    border-bottom: 0;
  }
`;

const StatusOptionButton = styled.button`
  cursor: pointer;
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${({ theme: { spacing } }) => `${spacing(2)} ${spacing(4)}`};
  border: 0;
  background: ${({ theme: { colors } }) => colors.black};

  &:hover,
  &:focus {
    background: ${({ theme: { colors } }) => colors.veryGray};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme: { spacing } }) => spacing(2.5)};
`;

const ErrorText = styled(Typography)`
  color: ${({ theme: { colors } }) => colors.firebreathingRed};
  margin-bottom: ${({ theme: { spacing } }) => spacing(3)};
`;

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const DATE_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
};

type LeadDetailContentProps = {
  leadId: string;
  onCancel: () => void;
  showBreadcrumb?: boolean;
};

export const LeadDetailContent = ({ leadId, onCancel, showBreadcrumb = true }: LeadDetailContentProps) => {
  const { hyrox365 } = useServices();
  const { data: lead, isPending, isError } = useQuery(leadDetailQueryOptions(hyrox365, leadId));

  if (!leadId) {
    return <ErrorComponent />;
  }

  if (isPending) {
    return <Loading />;
  }

  if (isError || !lead) {
    return <ErrorComponent />;
  }

  return (
    <LoadedLeadDetailContent
      key={lead.id}
      lead={lead}
      leadId={leadId}
      onCancel={onCancel}
      showBreadcrumb={showBreadcrumb}
    />
  );
};

type LoadedLeadDetailContentProps = {
  lead: LeadSummary;
  leadId: string;
  onCancel: () => void;
  showBreadcrumb: boolean;
};

type LeadStatusSelectProps = {
  label: string;
  value: LeadStatus;
  onChange: ({ id }: { id: string }) => void;
  disabled: boolean;
};

type LeadStatusOptionProps = {
  status: LeadStatus;
  selectedStatus: LeadStatus;
  listboxId: string;
  onSelect: (status: LeadStatus) => void;
};

const LeadStatusOption = ({ status, selectedStatus, listboxId, onSelect }: LeadStatusOptionProps) => (
  <StatusOption>
    <StatusOptionButton
      type="button"
      aria-selected={status === selectedStatus}
      id={`${listboxId}-${status}`}
      // biome-ignore lint/a11y/useSemanticElements: Custom options are needed to render status pills in the dropdown.
      role="option"
      onClick={() => onSelect(status)}
    >
      <LeadStatusPill status={status} testId={`lead-detail-status-option-${status}`} />
    </StatusOptionButton>
  </StatusOption>
);

const LeadStatusSelect = ({ label, value, onChange, disabled }: LeadStatusSelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listboxId = useId();
  const activeOptionId = `${listboxId}-${value}`;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleMouseDown = (event: MouseEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen((current) => !current);
    }
  };

  const handleSelect = (status: LeadStatus) => {
    setIsOpen(false);
    onChange({ id: status });
  };

  const handleStepSelect = (direction: 1 | -1) => {
    const currentIndex = STATUS_OPTIONS.indexOf(value);
    const nextIndex = (currentIndex + direction + STATUS_OPTIONS.length) % STATUS_OPTIONS.length;
    onChange({ id: STATUS_OPTIONS[nextIndex] });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) {
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setIsOpen(true);
      handleStepSelect(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if (isOpen && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      setIsOpen(false);
      return;
    }

    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <StatusSelectContainer ref={containerRef}>
      <StatusLabel variant="body2">{label}</StatusLabel>
      <StatusSelectButton
        type="button"
        aria-activedescendant={isOpen ? activeOptionId : undefined}
        aria-controls={listboxId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <LeadStatusPill status={value} testId={`lead-detail-status-selected-${value}`} />
        <img src={isOpen ? ChevronUpIcon : ChevronDownIcon} alt="" aria-hidden={true} />
      </StatusSelectButton>
      {isOpen ? (
        // biome-ignore lint/a11y/useSemanticElements: Native selects cannot render the status pill UI required here.
        <StatusOptions aria-label={label} id={listboxId} role="listbox">
          {STATUS_OPTIONS.map((status) => (
            <LeadStatusOption
              key={status}
              status={status}
              selectedStatus={value}
              listboxId={listboxId}
              onSelect={handleSelect}
            />
          ))}
        </StatusOptions>
      ) : null}
    </StatusSelectContainer>
  );
};

const LoadedLeadDetailContent = ({ lead, leadId, onCancel, showBreadcrumb }: LoadedLeadDetailContentProps) => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { hyrox365 } = useServices();
  const [draftStatus, setDraftStatus] = useState<LeadStatus | null>(null);
  const updateLeadStatus = useMutation({
    ...updateLeadStatusMutationOptions(hyrox365),
    onSuccess: async (_updatedLead, variables) => {
      trackEvent('leads_details_changed', { lead_id: variables.id, status: variables.status });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['leads', 'detail', leadId] }),
        queryClient.invalidateQueries({ queryKey: ['leads', 'list'] }),
      ]);
    },
    onError: () => {
      setDraftStatus(null);
    },
  });

  const selectedStatus = draftStatus ?? lead.status;
  const hasStatusChanged = selectedStatus !== lead.status;
  const formatDate = (value: string) => intl.formatDate(new Date(value), DATE_FORMAT_OPTIONS);
  const note = lead.message.trim() ? lead.message : '-';

  useEffect(() => {
    trackEvent('leads_details_viewed', { lead_id: lead.id });
  }, [lead.id]);

  const handleCancel = () => {
    onCancel();
  };

  const handleStatusChange = ({ id }: { id: string }) => {
    updateLeadStatus.reset();
    setDraftStatus(id as LeadStatus);
  };

  const handleSave = () => {
    updateLeadStatus.mutate({ id: lead.id, status: selectedStatus });
  };

  return (
    <PageContent>
      {showBreadcrumb ? (
        <Breadcrumb aria-label={intl.formatMessage({ id: 'leads.detail.breadcrumbLabel' })}>
          <BreadcrumbLink to={routes.LEADS}>{intl.formatMessage({ id: 'leads.pageTitle' })}</BreadcrumbLink>
          <span>{'>'}</span>
          <LeadBreadcrumbCurrent>{lead.athleteName}</LeadBreadcrumbCurrent>
        </Breadcrumb>
      ) : null}

      <ProfileHeader>
        <LeadInitialsAvatar aria-hidden={true}>{getInitials(lead.athleteName)}</LeadInitialsAvatar>
        <LeadHeaderText>
          <LeadTitle variant="h2">{lead.athleteName}</LeadTitle>
          <ContactRow>
            <ContactText variant="body2">{lead.athleteEmail}</ContactText>
            <ContactDivider />
            <ContactText variant="body2">{lead.athletePhone}</ContactText>
          </ContactRow>
        </LeadHeaderText>
      </ProfileHeader>

      <DetailGrid>
        <SectionTitle variant="h4">{intl.formatMessage({ id: 'leads.detail.title' })}</SectionTitle>
        <DetailRows>
          <DetailRow>
            <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.signedUpForHyrox' })}</Typography>
            <DetailValue variant="body2">
              {intl.formatMessage({ id: lead.signedUpForHyrox ? 'leads.detail.yes' : 'leads.detail.no' })}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.interestedIn' })}</Typography>
            <DetailValue variant="body2">{formatLeadInterestText(lead.interestedIn)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.appliedAt' })}</Typography>
            <DetailValue variant="body2">{formatDate(lead.receivedAt)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <Typography variant="body2">{intl.formatMessage({ id: 'leads.detail.lastUpdated' })}</Typography>
            <DetailValue variant="body2">{formatDate(lead.updatedAt)}</DetailValue>
          </DetailRow>
        </DetailRows>

        <NoteBlock>
          <NoteLabel variant="label">{intl.formatMessage({ id: 'leads.detail.note' })}</NoteLabel>
          <Typography variant="caption">{note}</Typography>
        </NoteBlock>

        <StatusField>
          <LeadStatusSelect
            label={intl.formatMessage({ id: 'leads.detail.status' })}
            value={selectedStatus}
            onChange={handleStatusChange}
            disabled={updateLeadStatus.isPending}
          />
        </StatusField>

        {updateLeadStatus.isError && (
          <ErrorText variant="caption">{intl.formatMessage({ id: 'leads.detail.saveError' })}</ErrorText>
        )}

        <ButtonRow>
          <Button
            label={intl.formatMessage({ id: 'leads.detail.cancel' })}
            type="secondary-white"
            onClick={handleCancel}
          />
          <Button
            label={intl.formatMessage({ id: 'leads.detail.saveChanges' })}
            type="primary"
            state={updateLeadStatus.isPending || !hasStatusChanged ? 'disabled' : 'default'}
            onClick={handleSave}
          />
        </ButtonRow>
      </DetailGrid>
    </PageContent>
  );
};

const LeadDetailPageContent = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { leadId } = useParams<{ leadId: string }>();

  if (!leadId) {
    return <ErrorComponent />;
  }

  return (
    <GenericPage pageTitle={intl.formatMessage({ id: 'leads.pageTitle' })}>
      <LeadDetailContent leadId={leadId} onCancel={() => navigate(routes.LEADS)} />
    </GenericPage>
  );
};

const LeadDetailPage = () => (
  <LeadsQueryProvider>
    <LeadDetailPageContent />
  </LeadsQueryProvider>
);

export default LeadDetailPage;
